import { WORD_BANK_DROPZONE_ID } from 'core/constants';
import { useMemo } from 'react';

const copyValues = (responseValues) =>
  responseValues.map((value) =>
    value instanceof Array ? value.slice() : value
  );

const removeIndex = (arr, index) => arr.filter((_, i) => i !== index);

// case insensitive Array.includes()
const iIncludes = (haystack, needle) =>
  haystack.map((item) => item?.toLowerCase()).indexOf(needle.toLowerCase());

export function useWordBank(options = [], responseValues = []) {
  // duplicate the responseValues and flatten them, so we can keep track of ones that have been used

  const usedOptionIndices = useMemo(() => {
    let tempResponseValues = copyValues(responseValues).flatMap(
      (value) => value
    );
    return options
      .map((option, index) => {
        const droppedIndex = iIncludes(tempResponseValues, option);
        const found = droppedIndex > -1;
        if (found) {
          // remove from the temp array
          tempResponseValues = tempResponseValues.filter(
            (_, i) => i !== droppedIndex
          );
        }
        return found ? index : NaN;
      })
      .filter((value) => !isNaN(value));
  }, [options, responseValues]);

  const remainingWords = options.filter(
    (_, index) => !usedOptionIndices.includes(index)
  );

  return { remainingWords, usedOptionIndices };
}

export function useClozeDragAndDrop(
  remainingWords,
  responseValues,
  onResponseChange,
  allowMultiple
) {
  const getWordBankIndex = (key) => {
    const match = key.match(/word_bank_item_(\d+)/);
    return (match && match[1]) || null;
  };

  const getDropzoneIndex = (key) => {
    const match = key.match(/dropzone_(\d+)/);
    return (match && match[1]) || null;
  };

  const getDroppedIndex = (key) => {
    const match = key.match(/dropped_(\d+)-?(\d+)?/);
    if (match) {
      const responseFieldIndex = Number(match[1]);
      const itemIndex = Number(match[2]);
      return [responseFieldIndex, itemIndex];
    }
    return [null, null];
  };

  const dropWord = (dropzoneIndex, draggingValue) => {
    const newResponseValues = copyValues(responseValues);
    if (allowMultiple) {
      newResponseValues[dropzoneIndex] = [
        ...(newResponseValues[dropzoneIndex] || []),
        draggingValue,
      ];
    } else {
      newResponseValues[dropzoneIndex] = draggingValue;
    }
    onResponseChange({ response_values: newResponseValues });
  };

  const returnToWordBank = (droppedIndex, itemIndex) => {
    const newResponseValues = copyValues(responseValues);
    if (allowMultiple) {
      newResponseValues[droppedIndex] = removeIndex(
        newResponseValues[droppedIndex],
        itemIndex
      );
    } else {
      newResponseValues[droppedIndex] = null;
    }

    onResponseChange({ response_values: newResponseValues });
  };

  const relocateWord = (draggingIndex, itemIndex, dropzoneIndex) => {
    const newResponseValues = copyValues(responseValues);
    if (allowMultiple) {
      newResponseValues[dropzoneIndex] = [
        ...(newResponseValues[dropzoneIndex] || []),
        newResponseValues[draggingIndex][itemIndex],
      ];
      newResponseValues[draggingIndex] = removeIndex(
        newResponseValues[draggingIndex],
        itemIndex
      );
    } else {
      newResponseValues[dropzoneIndex] = responseValues[draggingIndex];
      newResponseValues[draggingIndex] = null;
    }
    onResponseChange({ response_values: newResponseValues });
  };

  const recordMovement = (draggableId, dropzoneId, fromFieldIndex) => {
    const isRelocating = fromFieldIndex !== undefined;

    if (isRelocating) {
      // moving within the response fields
      const [draggingIndex, itemIndex] = getDroppedIndex(draggableId);
      const dropzoneIndex = getDropzoneIndex(dropzoneId);
      relocateWord(draggingIndex, itemIndex, dropzoneIndex);
    } else if (dropzoneId === WORD_BANK_DROPZONE_ID) {
      // returning to the palette
      const [draggingIndex, itemIndex] = getDroppedIndex(draggableId);
      returnToWordBank(draggingIndex, itemIndex);
    } else {
      // dragging from the word bank
      const wordBankIndex = getWordBankIndex(draggableId);
      const dropzoneIndex = getDropzoneIndex(dropzoneId);
      if (dropzoneIndex !== null) {
        dropWord(dropzoneIndex, remainingWords[wordBankIndex]);
      }
    }
  };

  return recordMovement;
}
